import {
  ApolloProvider,
  type ApolloClient,
  type NormalizedCacheObject,
} from "@apollo/client";
import { Card, NonIdealState } from "@blueprintjs/core";
import { createApolloClient } from "@package/ui-apollo";
import { useAuthState } from "@package/ui-authentication";
import { LoadingCard } from "@package/ui-components";
import {
  CONFIG_APP_TO_DISPLAY_NAME,
  useConfigState,
  type ConfigApp,
} from "@package/ui-config";
import { useOrganizationState } from "@package/ui-organization";
import { useEffect, useMemo, useState, type PropsWithChildren } from "react";

type ApolloProviderApiDataAccessProps = PropsWithChildren & {
  app: ConfigApp;
  organizationRequired?: boolean;
};

export function ApolloProviderApiDataAccess({
  app,
  organizationRequired = true,
  children,
}: ApolloProviderApiDataAccessProps) {
  const { authorizedApps, token } = useAuthState();
  const { apollo, baseUrlApi, environment } = useConfigState();
  const { loading, selectedOrganization } = useOrganizationState();
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();

  const appName = CONFIG_APP_TO_DISPLAY_NAME[app];
  const isLoadingOrganization = useMemo<boolean>(
    () => loading === "idle" || loading === "pending",
    [loading],
  );
  const isLoadingToken = useMemo<boolean>(() => !token, [token]);
  const isLoading = isLoadingOrganization || isLoadingToken;
  const isUnauthorized = !token || !authorizedApps.includes(app);
  const isNoOrganization = !selectedOrganization;

  useEffect(
    function effectCreateApolloClient() {
      const create = async () => {
        if (
          baseUrlApi &&
          environment &&
          token &&
          (!organizationRequired || !!selectedOrganization)
        ) {
          setClient(
            await createApolloClient({
              ...apollo,
              environment,
              organization: selectedOrganization?.orgId,
              token,
              uri: `${baseUrlApi}/api/graphql`,
            }),
          );
        }
      };

      create();
    },
    [
      apollo,
      baseUrlApi,
      environment,
      organizationRequired,
      selectedOrganization,
      token,
    ],
  );

  if (isLoading || !client) {
    return <LoadingCard title={CONFIG_APP_TO_DISPLAY_NAME[app]} />;
  }

  if (isUnauthorized) {
    return (
      <div className="flex justify-center">
        <div className="mt-8">
          <Card>
            <NonIdealState
              icon="warning-sign"
              title="Not Authorized"
              description={`You do not have permissions to access the PanoScape ${appName} Interface`}
            />
          </Card>
        </div>
      </div>
    );
  }

  if (isNoOrganization && organizationRequired) {
    return (
      <div className="flex justify-center">
        <div className="mt-8">
          <Card>
            <NonIdealState
              icon="warning-sign"
              title="No Access"
              description={`You do not have access to any resources in the PanoScape ${appName} Interface`}
            />
          </Card>
        </div>
      </div>
    );
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
